import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { siteConfig } from '@/config/siteConfig';

export default function Contact() {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-20" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1e3a8a] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Get in Touch
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Have questions or ready to start your home search? I'm here to help. Reach out anytime.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-20 bg-white" data-testid="contact-info-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <Card className="hover:shadow-xl transition-shadow" data-testid="contact-phone-card">
              <CardContent className="pt-6 text-center">
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-[#1e3a8a]/10 rounded-full">
                    <Phone className="h-8 w-8 text-[#1e3a8a]" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-[#1e3a8a] mb-2">Phone</h3>
                <p className="text-slate-600">{siteConfig.phone}</p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow" data-testid="contact-email-card">
              <CardContent className="pt-6 text-center">
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-[#1e3a8a]/10 rounded-full">
                    <Mail className="h-8 w-8 text-[#1e3a8a]" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-[#1e3a8a] mb-2">Email</h3>
                <p className="text-slate-600">{siteConfig.email}</p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow" data-testid="contact-location-card">
              <CardContent className="pt-6 text-center">
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-[#1e3a8a]/10 rounded-full">
                    <MapPin className="h-8 w-8 text-[#1e3a8a]" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-[#1e3a8a] mb-2">Locations</h3>
                <p className="text-slate-600">CA & FL</p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow" data-testid="contact-hours-card">
              <CardContent className="pt-6 text-center">
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-[#1e3a8a]/10 rounded-full">
                    <Clock className="h-8 w-8 text-[#1e3a8a]" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-[#1e3a8a] mb-2">Hours</h3>
                <p className="text-slate-600">{siteConfig.businessHours}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-slate-50" data-testid="map-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1e3a8a] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Office Locations
            </h2>
            <p className="text-lg text-slate-600">
              Visit us at our California or Florida offices
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* California Map */}
            <Card className="overflow-hidden" data-testid="california-map-card">
              <CardContent className="p-0">
                <div className="h-96">
                  <iframe
                    title="California Office"
                    src={siteConfig.californiaOffice.mapEmbedUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-[#1e3a8a] mb-2">California Office</h3>
                  <p className="text-slate-600">{siteConfig.californiaOffice.address}</p>
                </div>
              </CardContent>
            </Card>

            {/* Florida Map */}
            <Card className="overflow-hidden" data-testid="florida-map-card">
              <CardContent className="p-0">
                <div className="h-96">
                  <iframe
                    title="Florida Office"
                    src={siteConfig.floridaOffice.mapEmbedUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-[#1e3a8a] mb-2">Florida Office</h3>
                  <p className="text-slate-600">{siteConfig.floridaOffice.address}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1e3a8a] text-white" data-testid="contact-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8">
            Fill out our quick signup form and I'll reach out to discuss your home buying needs.
          </p>
          <Button
            data-testid="contact-cta-signup-btn"
            onClick={() => window.location.href = '/signup'}
            size="lg"
            className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-[#1e3a8a] text-lg px-8 py-6 font-semibold"
          >
            Complete Signup Form
          </Button>
        </div>
      </section>
    </div>
  );
}
